﻿import React, { createContext, useContext } from 'react';
import type {
    AirportSuggestion,
    CrosswindResult,
    RunwayAnalysisRow,
    RunwayGhost,
    RunwayPair,
    RunwayRankingOptions,
    TafHourSlot,
    TafPeriod,
    WindData,
    WindSources,
    WindSourceType
} from './types';

export type { TafHourSlot };

export function headingToLabel(deg: number): string {
    const h = ((deg % 360) + 360) % 360;
    const num = Math.round(h / 10) % 36;
    return String(num === 0 ? 36 : num).padStart(2, '0');
}

function t(deg: number): number {
    const h = ((deg % 360) + 360) % 360;
    const num = Math.round(h / 10) % 36;
    return num === 0 ? 360 : num * 10;
}
export function primaryHeading(deg: number): number {
    const recipDeg = reciprocal(deg);
    return Math.min(t(deg), t(recipDeg));
}

export function reciprocal(deg: number): number {
    return (deg + 180) % 360;
}

export const SOURCE_LABELS: Record<WindSourceType, string> = {
    metar: 'METAR', datis: 'D-ATIS', itws: 'ITWS', taf: 'TAF', user: 'User'
};


export function buildHourlySlots(periods: TafPeriod[]): TafHourSlot[] | null {
    if (!periods.every(p => p.validFrom && p.validTo)) { return null; }

    // Check whether slots will span multiple UTC days.
    let firstDay: number | null = null;
    let hasMultipleDays = false;
    for (const period of periods) {
        const from = new Date(period.validFrom!);
        const to   = new Date(period.validTo!);
        for (const d of [from, to]) {
            const day = d.getUTCDate();
            if (firstDay === null) { firstDay = day; }
            else if (day !== firstDay) { hasMultipleDays = true; }
        }
    }

    const slots: TafHourSlot[] = [];
    for (const period of periods) {
        const from = new Date(period.validFrom!);
        const to   = new Date(period.validTo!);
        const cur  = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate(), from.getUTCHours()));
        const end  = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate(), to.getUTCHours()));
        while (cur < end) {
            const hh = String(cur.getUTCHours()).padStart(2, '0');
            const timeStr = hasMultipleDays ? `${hh}:00Z/${cur.getUTCDate()}` : `${hh}:00Z`;
            const { direction: dir, speed, gust } = period.wind;
            slots.push({
                label: `${timeStr}  —  ${dir}° / ${speed}kt${gust ? ` G${gust}` : ''}`,
                wind: period.wind,
                hourUtc: new Date(cur)
            });
            cur.setUTCHours(cur.getUTCHours() + 1);
        }
    }

    if (slots.length === 0) { return null; }

    slots.sort((a, b) => a.hourUtc.getTime() - b.hourUtc.getTime());
    const deduped: TafHourSlot[] = [];
    let lastTime = NaN;
    for (const slot of slots) {
        const time = slot.hourUtc.getTime();
        if (time !== lastTime) {
            deduped.push(slot);
            lastTime = time;
        }
    }
    return deduped;
}

export function defaultSlotIndex(slots: TafHourSlot[]): number {
    const n = new Date();
    const now = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate(), n.getUTCHours())).getTime();
    let idx = 0;
    for (let i = 0; i < slots.length; i++) {
        if (slots[i].hourUtc.getTime() <= now) { idx = i; }
    }
    return idx;
}

export interface CrosswindCtx {
    heading: number;
    setHeading: React.Dispatch<React.SetStateAction<number>>;
    flipped: boolean;
    setFlipped: React.Dispatch<React.SetStateAction<boolean>>;
    primaryLabel: string;
    reciprocalLabel: string;
    magneticVariation: number;
    headingMode: 'magnetic' | 'true';
    icao?: string;
    iata?: string;
    showMagVarIndicator: boolean;
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    scale: number;
    runways?: RunwayPair[];
    selectedRunwayPair: RunwayPair | null;
    setSelectedRunwayPair: (pair: RunwayPair | null) => void;
    selectedSource: WindSourceType;
    setSelectedSource: (source: WindSourceType) => void;
    selectedTafIndex: number;
    setSelectedTafIndex: React.Dispatch<React.SetStateAction<number>>;
    selectedTafHourIndex: number;
    setSelectedTafHourIndex: React.Dispatch<React.SetStateAction<number>>;
    gustFactor: number;
    setGustFactor: React.Dispatch<React.SetStateAction<number>>;
    userWind: WindData;
    setUserWind: React.Dispatch<React.SetStateAction<WindData>>;
    activeWind: WindData;
    result: CrosswindResult;
    runwayRows: RunwayAnalysisRow[];
    runwayGhosts: RunwayGhost[];
    runwayRanking?: RunwayRankingOptions;
    showBestRunway: boolean;
    showRunwayBars: boolean;
    showRunwayUsage: boolean;
    hoveredRunwayId: string | null;
    setHoveredRunwayId: React.Dispatch<React.SetStateAction<string | null>>;
    selectRunway: (row: RunwayAnalysisRow) => void;
    tafHourlySlots: TafHourSlot[] | null;
    windSources?: WindSources;
    availableSources: WindSourceType[];
    itwsSubSource: 'runway' | 'centerfield';
    setItwsSubSource: React.Dispatch<React.SetStateAction<'runway' | 'centerfield'>>;
    itwsRunwayOptions: string[];
    itwsRunwayId: string | null;
    setItwsRunwayId: React.Dispatch<React.SetStateAction<string | null>>;
    itwsEffectiveRunwayId: string | null;
    itwsFlightType: 'arrival' | 'departure';
    setItwsFlightType: React.Dispatch<React.SetStateAction<'arrival' | 'departure'>>;
    onAirportSearch?: (query: string) => void;
    airportSuggestions?: AirportSuggestion[];
    onAirportSelect?: (airport: AirportSuggestion) => void;
}

export const CrosswindContext = createContext<CrosswindCtx | null>(null);

export function useCrosswind(): CrosswindCtx {
    const ctx = useContext(CrosswindContext);
    if (!ctx) { throw new Error('Must be used inside CrosswindCalculator'); }
    return ctx;
}
